import React from 'react';
import { Form, Select, Input, InputNumber, Button, Radio, Space } from 'antd';
import {CustomerBillDTO, FspDto, FspType} from "@/shared/api/model";

const { Option } = Select;

interface PaymentFormProps {
    form: any;
    currentFspType: FspType;
    onFspTypeChange: (type: FspType) => void;
    onFspChange: (code: string) => void;
    fsps?: FspDto[];
    isLoadingFsps: boolean;
    onPhoneNumberChange: (value: string) => void;
    showOtpInput: boolean;
    isLinkedNumber: boolean;
    onAmountChange: (value: string) => void;
    loading: boolean;
    dark: boolean;
    onSubmit: (values: any) => void;
    selectedBill?: CustomerBillDTO;
}

export const PaymentForm: React.FC<PaymentFormProps> = ({
                                                            form,
                                                            currentFspType,
                                                            onFspTypeChange,
                                                            onFspChange,
                                                            fsps,
                                                            isLoadingFsps,
                                                            onPhoneNumberChange,
                                                            showOtpInput,
                                                            isLinkedNumber,
                                                            onAmountChange,
                                                            loading,
                                                            dark,
                                                            onSubmit,
                                                            selectedBill
                                                        }) => {
    const isLoan = selectedBill?.productType === 'L';

    // Handle amount type change
    const handleAmountTypeChange = (e: any) => {
        const amountType = e.target.value;
        let amount;

        switch(amountType) {
            case 'due':
                amount = selectedBill?.amount;
                break;
            case 'full':
                amount = selectedBill?.balanceAmount;
                break;
            case 'custom':
                amount = undefined;
                form.setFieldsValue({ amount: undefined });
                break;
            default:
                amount = undefined;
        }

        if (amount !== undefined) {
            form.setFieldsValue({ amount });
            onAmountChange(amount.toString());
        } else if (amountType === 'custom') {
            onAmountChange('');
        }
    };

    return (
        <Form form={form} onFinish={onSubmit} layout="vertical" initialValues={{ paymentType: FspType.MNO }}>
            <Form.Item name="paymentType" label="Payment Type" rules={[{ required: true }]}>
                <Select onChange={(value) => onFspTypeChange(value as FspType)}>
                    <Option value={FspType.MNO}>Mobile Money</Option>
                    <Option value={FspType.BANK}>Bank Transfer</Option>
                </Select>
            </Form.Item>

            <Form.Item name="channelId" label="Payment Provider" rules={[{ required: true }]}>
                <Select
                    showSearch
                    loading={isLoadingFsps}
                    onChange={onFspChange}
                    placeholder={`Select ${currentFspType === FspType.BANK ? 'Bank' : 'Mobile Money Provider'}`}
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                        // @ts-ignore
                        (option?.children as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                    }
                >
                    {fsps?.map((fsp) => (
                        <Option key={fsp.code} value={fsp.code}>
                            {fsp.name || fsp.institutionName}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            {currentFspType === FspType.MNO && (
                <>
                    <Form.Item
                        name="phoneNumber"
                        label="Phone Number"
                        rules={[{ required: true }]}
                        extra={isLinkedNumber ? "This number is linked to your account" : ""}
                    >
                        <Input onChange={(e) => onPhoneNumberChange(e.target.value)} />
                    </Form.Item>

                    {showOtpInput && (
                        <Form.Item name="otp" label="OTP" rules={[{ required: true }]}>
                            <Input placeholder="Enter OTP sent to your phone" />
                        </Form.Item>
                    )}
                </>
            )}

            {isLoan && (
                <Form.Item name="amountType" label="Payment Amount Type" rules={[{ required: true }]}>
                    <Radio.Group onChange={handleAmountTypeChange}>
                        <Space direction="vertical">
                            <Radio value="due">Due Amount ({selectedBill?.amount?.toLocaleString()} TZS)</Radio>
                            <Radio value="full">Full Balance ({selectedBill?.balanceAmount?.toLocaleString()} TZS)</Radio>
                            <Radio value="custom">Custom Amount</Radio>
                        </Space>
                    </Radio.Group>
                </Form.Item>
            )}

            <Form.Item
                name="amount"
                label="Amount"
                rules={[
                    { required: true },
                    {
                        validator: (_, value) => {
                            // @ts-ignore
                            if (isLoan && value > selectedBill?.balanceAmount) {
                                return Promise.reject('Amount cannot exceed the balance amount');
                            }
                            return Promise.resolve();
                        }
                    }
                ]}
                dependencies={['amountType']}
            >
                <InputNumber
                    style={{ width: "100%" }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    parser={(value) => value!.replace(/TZS\s?|(,*)/g, "")}
                    onChange={(value) => onAmountChange(value?.toString() || '')}
                    className={dark ? 'dark-mode-input' : ''}
                    disabled={isLoan && ['due', 'full'].includes(form.getFieldValue('amountType'))}
                />
            </Form.Item>

            <Form.Item>
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                >
                    {currentFspType === FspType.MNO ?
                        (showOtpInput ? 'Verify & Pay' : 'Proceed with Payment') :
                        'Get Payment Instructions'}
                </Button>
            </Form.Item>
        </Form>
    );
};

export default PaymentForm;